import React, { useState, useEffect } from 'react';
import { Row, Col, Spinner, Table, Tab, Tabs, Modal, Button, Form } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';
import '../Turnero.css';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle, faSignOutAlt, faFileExcel, faChartLine, faExchangeAlt, faCheckCircle, faClock, faUserCircle, faHistory, faUsers } from '@fortawesome/free-solid-svg-icons';

/**
 * StaffConsole - Main interface for staff attending turns
 */
const StaffConsole = ({ ipserv, user, typeId, sucursalId, onLogout }) => {
    const [loading, setLoading] = useState(false);
    const [queue, setQueue] = useState([]);
    const [history, setHistory] = useState([]);
    const [currentTurn, setCurrentTurn] = useState(null);
    const [averageAttentionTime, setAverageAttentionTime] = useState(null);
    const [attentionDuration, setAttentionDuration] = useState(0); // Seconds since attention started

    // Modal & References State
    const [showModal, setShowModal] = useState(false);
    const [references, setReferences] = useState({ ref1: '', ref2: '', obs: '' });
    const [availableTypes, setAvailableTypes] = useState([]);
    const [targetType, setTargetType] = useState('');

    // --- NEW SECTOR DASHBOARD STATES ---
    const [activeType, setActiveType] = useState(null);
    const [multiCounts, setMultiCounts] = useState({});
    const [backendError, setBackendError] = useState(null); // State for backend compatibility warnings
    const [subscriptionStatus, setSubscriptionStatus] = useState({ isExpired: false, daysLeft: 0 });

    // Normalize typeId to array of strings
    const typeIdsArray = typeId ? String(typeId).split(',') : [];

    const checkSubscription = () => {
        if (!sucursalId) return;
        request.Get(ipserv + 'turnero/subscription-status-by-branch', { sucursalId }, r => {
            if (r.success) {
                setSubscriptionStatus({
                    isExpired: r.isExpired,
                    daysLeft: r.daysLeft
                });
            }
        });
    };

    // Initialize activeType from the first ID in the list
    useEffect(() => {
        if (typeIdsArray.length > 0 && !activeType) {
            setActiveType(typeIdsArray[0]);
        }
    }, [typeId]);

    const loadQueue = () => {
        const typeToLoad = activeType || typeId;
        if (!typeToLoad) return;
        request.Get(ipserv + 'getQueueForStaff', { typeId: typeToLoad, sucursalId }, r => {
            if (r.success) setQueue(r.data || []);
        });
    };

    const loadHistory = () => {
        const typeToLoad = activeType || typeId;
        if (!typeToLoad || !user?.UsrId) return;
        request.Get(ipserv + 'getTurneroHistory', { typeId: typeToLoad, usrId: user.UsrId, sucursalId }, r => {
            if (r.success) setHistory(r.data || []);
        });
    };

    const loadTypes = () => {
        request.Get(ipserv + 'getTurneroTipos', { sucursalId }, r => {
            if (r.success) setAvailableTypes(r.rows || []);
        });
    }

    const loadMultiCounts = (intervalToClear = null) => {
        if (!typeId) return;
        request.Get(ipserv + 'getMultiQueueCounts', { typeIds: typeId, sucursalId }, r => {
            if (r.success) {
                setMultiCounts(r.counts || {});
                setBackendError(null);
            } else {
                // If the endpoint doesn't exist (404) or fails, we show a warning and clear the interval
                setBackendError("El servidor no soporta el Dashboard Multi-Sector. Se requiere actualización del Backend.");
                if (intervalToClear) clearInterval(intervalToClear);
            }
        });
    };

    useEffect(() => {
        checkSubscription();
        loadQueue();
        loadHistory();
        loadTypes();

        // Initial load
        loadMultiCounts();

        const queueInterval = setInterval(loadQueue, 5000);
        const subscriptionInterval = setInterval(checkSubscription, 60000); // Check once per minute
        const countsInterval = setInterval(() => {
            loadMultiCounts(countsInterval);
        }, 5000);

        return () => {
            clearInterval(queueInterval);
            clearInterval(subscriptionInterval);
            clearInterval(countsInterval);
        };
    }, [activeType, typeId, sucursalId]);

    const handleTakeNext = () => {
        if (subscriptionStatus.isExpired) {
            Notify({ type: 'warning', text: 'Suscripción vencida. No se pueden realizar acciones de atención.' });
            return;
        }
        // If already attending, force modal to enter data
        if (currentTurn) {
            setShowModal(true);
            return;
        }

        setLoading(true);
        request.Post(ipserv + 'takeNextTurn', { typeId: activeType, usrId: user.UsrId, sucursalId }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Llamando al turno ' + r.data.TurnoNumero });
                setCurrentTurn(r.data);
                loadQueue();
                loadHistory();
            } else {
                Notify({ type: 'info', text: 'No hay turnos en cola' });
            }
            setLoading(false);
        });
    };

    const handleFinishAndNext = () => {
        setLoading(true);
        const payload = {
            typeId: activeType || typeId,
            usrId: user.UsrId,
            sucursalId,
            finishTurnId: currentTurn.TurnoId,
            references: references
        };

        // If the current turn is from a different sector, we prefer to call the new finish endpoint first 
        // to avoid the takeNextTurn sector-mismatch bug, then take the next one from the active sector.
        if (String(currentTurn.TipoId) !== String(activeType || typeIdsArray[0])) {
            request.Post(ipserv + 'finishTurn', { turnoId: currentTurn.TurnoId, sucursalId, references }, rClose => {
                if (rClose.success) {
                    // Now take the next one from the desired sector
                    request.Post(ipserv + 'takeNextTurn', { typeId: activeType || typeId, usrId: user.UsrId, sucursalId }, rNext => {
                        if (rNext.success) {
                            Notify({ type: 'success', text: 'Turno previo finalizado. Llamando al nuevo turno.' });
                            setCurrentTurn(rNext.data);
                        } else {
                            Notify({ type: 'success', text: 'Turno previo finalizado. No hay más turnos en cola en este sector.' });
                            setCurrentTurn(null);
                        }
                        resetModal();
                        loadQueue();
                        loadHistory();
                        setLoading(false);
                    });
                } else {
                    Notify({ type: 'fail', text: 'Error al finalizar el turno previo.' });
                    setLoading(false);
                }
            });
            return;
        }

        request.Post(ipserv + 'takeNextTurn', payload, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Turno finalizado. Llamando al turno ' + r.data.TurnoNumero });
                setCurrentTurn(r.data);
                resetModal();
                loadQueue();
                loadHistory();
            } else {
                // If there's no next turn, at least we finished the current one
                Notify({ type: 'success', text: 'Turno finalizado. No hay más turnos en cola.' });
                setCurrentTurn(null);
                resetModal();
                loadQueue();
                loadHistory();
            }
            setLoading(false);
        });
    }

    const handleFinishOnly = () => {
        if (!currentTurn) return;
        setLoading(true);

        request.Post(ipserv + 'finishTurn', { 
            turnoId: currentTurn.TurnoId, 
            sucursalId, 
            references 
        }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Atención finalizada correctamente' });
                setCurrentTurn(null);
                resetModal();
                loadQueue();
                loadHistory();
            } else {
                Notify({ type: 'fail', text: 'Error al finalizar atención' });
            }
            setLoading(false);
        });
    };

    const handleDeriveTurn = () => {
        if (!targetType) {
            Notify({ type: 'info', text: 'Seleccione un sector para derivar' });
            return;
        }

        setLoading(true);
        const payload = {
            turnoId: currentTurn.TurnoId,
            targetTypeId: targetType,
            references: references,
            usrId: user.UsrId,
            sucursalId,
            typeId: activeType || typeId
        };

        request.Post(ipserv + 'deriveTurn', payload, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Turno derivado correctamente' });
                setCurrentTurn(null);
                resetModal();
                loadQueue();
                loadHistory();
            } else {
                Notify({ type: 'fail', text: 'Error al derivar turno' });
            }
            setLoading(false);
        });
    }

    const resetModal = () => {
        setShowModal(false);
        setReferences({ ref1: '', ref2: '', obs: '' });
        setTargetType('');
    }

    const handleManualTake = (turnoId) => {
        if (subscriptionStatus.isExpired) {
            Notify({ type: 'warning', text: 'Suscripción vencida. Modo lectura activado.' });
            return;
        }
        if (currentTurn) {
            Notify({ type: 'info', text: 'Primero debe finalizar el turno actual' });
            return;
        }
        setLoading(true);
        request.Post(ipserv + 'manualTakeTurn', { turnoId, usrId: user.UsrId, sucursalId }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Llamando turno seleccionado' });
                setCurrentTurn(r.data);
                loadQueue();
                loadHistory();
            }
            setLoading(false);
        });
    };

    const handleAverageAttentionTime = () => {
        setLoading(true);
        request.Post(ipserv + 'getAverageAttentionTime', { usrId: user.UsrId, sucursalId }, r => {
            if (r.success) {
                setAverageAttentionTime(r.data[0].tiempoPromedio);
            }
            setLoading(false);
        });
    };

    // Live Timer for Attention
    useEffect(() => {
        let interval = null;
        if (currentTurn) {
            setAttentionDuration(0);
            interval = setInterval(() => {
                setAttentionDuration(prev => prev + 1);
            }, 1000);
        } else {
            setAttentionDuration(0);
        }
        return () => clearInterval(interval);
    }, [currentTurn]);

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleExportCSV = () => {
        if (history.length === 0) {
            Notify({ type: 'info', text: 'No hay historial para exportar' });
            return;
        }

        const activeSector = availableTypes.find(t => String(t.TipoId) === String(activeType || typeIdsArray[0]));
        const sectorName = activeSector?.TipoNombre || 'Sector';

        // Headers
        let csvContent = "Numero,Fecha Llamado,Tiempo Atencion,Estado,Referencia 1,Referencia 2,Observaciones\n";

        // Rows
        history.forEach(row => {
            csvContent += `${row.TurnoNumero},${row.TurnoFchLlamado},${row.TiempoAtencion || '---'},Finalizado,${row.Ref1 || ''},${row.Ref2 || ''},${row.Observaciones || ''}\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `Reporte_${sectorName}_${new Date().toLocaleDateString()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        Notify({ type: 'success', text: 'Reporte descargado correctamente' });
    };

    // Process data for charts
    const getStatsData = () => {
        // Dummy data for volume trend (last 7 points)
        const volumeData = history.slice(0, 10).reverse().map((t, idx) => ({
            name: t.TurnoNumero,
            atencion: parseInt(t.TiempoAtencion) || 0,
            volumen: idx + 1
        }));

        return volumeData;
    };

    useEffect(() => {
        handleAverageAttentionTime();
    }, [queue]);

    return (
        <div className="turnero-container p-4">
            <header className="d-flex justify-content-between align-items-center mb-4 animate-in">
                <div className="d-flex align-items-center">
                    <div className="user-avatar-premium mr-3">
                        <FontAwesomeIcon icon={faUserCircle} size="3x" className="text-primary opacity-80" />
                    </div>
                    <div>
                        <h2 className="turnero-title m-0">Consola de Atención</h2>
                        <p className="text-muted m-0">
                            {user.UsrNombre} {user.UsrApellido} | <span className="text-primary-light">ID: {user.UsrDni}</span>
                            <span className="ml-3 badge-premium">
                                <FontAwesomeIcon icon={faUsers} className="mr-2" />
                                {user.SucursalNombre || 'Sucursal Principal'}
                            </span>
                        </p>
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <div className="text-right mr-4 d-none d-md-block">
                        <div className="text-muted small font-weight-bold">SESIÓN ACTIVA</div>
                        <div className="text-main h5 m-0 font-weight-bold">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                    <button className="turnero-btn turnero-btn-danger shadow-sm" onClick={onLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" /> SALIR
                    </button>
                    {user?.ProfileId && (
                        <button 
                            className="btn-premium-solid ml-3 shadow-sm" 
                            onClick={() => window.location.href = '/datamanager/Core/Admin'}
                            style={{ height: '40px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                        >
                            <FontAwesomeIcon icon={faChartLine} className="mr-2" /> PANEL DE REPORTES
                        </button>
                    )}
                </div>
            </header>

            {/* SUBSCRIPTION WARNING BANNER */}
            {subscriptionStatus.isExpired && (
                <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="alert-premium-danger mb-4 d-flex align-items-center justify-content-between"
                >
                    <div>
                        <FontAwesomeIcon icon={faClock} className="mr-3 h4 mb-0" />
                        <span>
                            <strong>Suscripción Vencida:</strong> El sistema se encuentra en modo <strong>Solo Lectura</strong>. 
                            Contacte a su administrador para renovar el servicio.
                        </span>
                    </div>
                    <button className="btn-premium-outline btn-sm" onClick={checkSubscription}>
                        REINTENTAR
                    </button>
                </motion.div>
            )}

            {/* SECTOR DASHBOARD - MULTI-SECTOR CARDS */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
            >
                {backendError && (
                    <div className="alert-premium-warning mb-3">
                        <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
                        <small><b>Aviso:</b> {backendError}</small>
                    </div>
                )}
                <div className="d-flex flex-wrap" style={{ gap: '15px' }}>
                    {typeIdsArray.map((tid, idx) => {
                        if (!tid) return null;
                        const typeInfo = availableTypes.find(t => String(t.TipoId) === String(tid));
                        const count = multiCounts[tid] || 0;
                        const isActive = String(activeType || typeIdsArray[0]) === String(tid);

                        return (
                            <motion.div
                                key={tid}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: idx * 0.05 }}
                                className={`sector-card-premium ${isActive ? 'active' : ''} ${count > 5 ? 'high-demand' : ''}`}
                                onClick={() => setActiveType(tid)}
                            >
                                <div className="sector-info">
                                    <span className="sector-name">{typeInfo?.TipoNombre || 'Cargando...'}</span>
                                    <span className="sector-acronym">{typeInfo?.TipoAcronimo}</span>
                                </div>
                                <div className="sector-queue">
                                    <span className="queue-count">{count}</span>
                                    <span className="queue-label">EN ESPERA</span>
                                </div>
                                {isActive && <div className="active-glow"></div>}
                            </motion.div>
                        );
                    })}
                </div>
            </motion.div>

            <Row>
                {/* Main Action Block - Premium Design */}
                <Col lg={8} md={12} className="mb-4">
                    <div className="turnero-attending-card-premium animate-in">
                        <div className="attending-inner">
                            <div className="attending-content">
                                <div className="attending-status">
                                    <span className="pulse-indicator"></span>
                                    {currentTurn ? 'TRABAJANDO EN TURNO' : 'LISTO PARA ATENDER'}
                                </div>

                                <AnimatePresence mode="wait">
                                    {currentTurn ? (
                                        <motion.div
                                            key={currentTurn.TurnoId}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            className="attending-active"
                                        >
                                            <div className="d-flex align-items-end">
                                                <div className="attending-number">{currentTurn.TurnoNumero}</div>
                                                <div className="attending-timer ml-4 mb-4">
                                                    <FontAwesomeIcon icon={faClock} className="mr-2 text-primary opacity-50" />
                                                    {formatDuration(attentionDuration)}
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <div className="attending-sector-label-premium">
                                                    {availableTypes.find(t => String(t.TipoId) === String(currentTurn.TipoId))?.TipoNombre}
                                                </div>
                                                {String(currentTurn.TipoId) !== String(activeType || typeIdsArray[0]) && (
                                                    <span className="badge-warning-premium ml-3">
                                                        SECTOR DIFERENTE
                                                    </span>
                                                )}
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="empty"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="attending-empty"
                                        >
                                            <div className="empty-title">Sin actividad</div>
                                            <div className="empty-subtitle">Seleccione "Llamar Próximo" para comenzar</div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="attending-actions">
                                <button
                                    className={`call-next-btn-premium ${subscriptionStatus.isExpired ? 'disabled' : ''}`}
                                    onClick={handleTakeNext}
                                    disabled={loading || subscriptionStatus.isExpired}
                                >
                                    <div className="btn-shine"></div>
                                    <FontAwesomeIcon icon={faPlayCircle} className="mr-3" />
                                    <span>{currentTurn ? 'FINALIZAR / DERIVAR' : 'LLAMAR PRÓXIMO'}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="turnero-card-premium mt-4">
                        <Tabs defaultActiveKey="queue" className="turnero-tabs-premium border-0">
                            <Tab eventKey="queue" title={<span><FontAwesomeIcon icon={faUsers} className="mr-2" /> EN COLA</span>} className="p-0">
                                <div className="table-responsive-premium">
                                    <Table hover className="m-0 turnero-table">
                                        <thead>
                                            <tr>
                                                <th>NÚMERO</th>
                                                <th>CLIENTE</th>
                                                <th>SITUACIÓN</th>
                                                <th className="text-right">ACCIONES</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {queue.length === 0 ? (
                                                <tr><td colSpan="4" className="text-center py-5 text-muted h6">No hay turnos pendientes en este sector</td></tr>
                                            ) : (
                                                queue.map((t, idx) => (
                                                    <tr key={t.TurnoId} className="animate-in" style={{ animationDelay: `${idx * 0.05}s` }}>
                                                        <td className="font-weight-bold text-primary">{t.TurnoNumero}</td>
                                                        <td>{t.TurnoNombreCliente || 'No especificado'}</td>
                                                        <td><span className="status-pill waiting">En espera</span></td>
                                                        <td className="text-right">
                                                            <button 
                                                                className={`btn-action-premium ${subscriptionStatus.isExpired ? 'disabled' : ''}`} 
                                                                onClick={() => handleManualTake(t.TurnoId)}
                                                                disabled={subscriptionStatus.isExpired}
                                                            >
                                                                ATENDER
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Tab>
                            <Tab eventKey="history" title={<span><FontAwesomeIcon icon={faHistory} className="mr-2" /> MI HISTORIAL</span>} className="p-0">
                                <div className="table-responsive-premium">
                                    <Table hover className="m-0 turnero-table">
                                        <thead>
                                            <tr>
                                                <th>NÚMERO</th>
                                                <th>HORA</th>
                                                <th>DURACIÓN</th>
                                                <th>ESTADO</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.length === 0 ? (
                                                <tr><td colSpan="4" className="text-center py-5 text-muted h6">Aún no has atendido turnos hoy</td></tr>
                                            ) : (
                                                history.slice(0, 15).map((t, idx) => (
                                                    <tr key={t.TurnoId} className="animate-in" style={{ animationDelay: `${idx * 0.05}s` }}>
                                                        <td className="font-weight-bold">{t.TurnoNumero}</td>
                                                        <td className="text-muted">{new Date(t.TurnoFchLlamado).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                                        <td>{t.TiempoAtencion ? `${t.TiempoAtencion} min` : '---'}</td>
                                                        <td><span className="status-pill completed">Atendido</span></td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Tab>
                            <Tab eventKey="stats" title={<span><FontAwesomeIcon icon={faChartLine} className="mr-2" /> ESTADÍSTICAS</span>} className="p-4">
                                <Row>
                                    <Col md={6}>
                                        <div className="stat-chart-container">
                                            <h6 className="text-muted mb-4 text-center">TENDENCIA TIEMPOS (MIN)</h6>
                                            <div style={{ width: '100%', height: 200 }}>
                                                <ResponsiveContainer>
                                                    <AreaChart data={getStatsData()}>
                                                        <defs>
                                                            <linearGradient id="colorAt" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="var(--turnero-primary)" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="var(--turnero-primary)" stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                                        <XAxis dataKey="name" stroke="var(--text-dim)" fontSize={10} />
                                                        <YAxis stroke="var(--text-dim)" fontSize={10} />
                                                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '8px' }} />
                                                        <Area type="monotone" dataKey="atencion" stroke="var(--primary)" fillOpacity={1} fill="url(#colorAt)" />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="stat-chart-container">
                                            <h6 className="text-muted mb-4 text-center">VOLUMEN POR TURNO</h6>
                                            <div style={{ width: '100%', height: 200 }}>
                                                <ResponsiveContainer>
                                                    <BarChart data={getStatsData()}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                                        <XAxis dataKey="name" stroke="var(--text-dim)" fontSize={10} />
                                                        <YAxis stroke="var(--text-dim)" fontSize={10} />
                                                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '8px' }} />
                                                        <Bar dataKey="volumen" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Tab>
                        </Tabs>
                    </div>
                </Col>

                {/* Metrics Sidebar */}
                <Col lg={4} md={12}>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="turnero-card-premium p-4 h-100 d-flex flex-column"
                    >
                        <h5 className="mb-4 text-primary font-weight-bold d-flex align-items-center">
                            <FontAwesomeIcon icon={faChartLine} className="mr-2" /> MÉTRICAS DEL DÍA
                        </h5>

                        <div className="metric-box-premium mb-4">
                            <div className="text-muted small font-weight-bold">CLIENTES ATENDIDOS</div>
                            <div className="h1 font-weight-bold text-main m-0">{history.length}</div>
                            <div className="metric-trend positive">+12% vs ayer</div>
                        </div>

                        <div className="metric-box-premium mb-4 highlight">
                            <div className="text-muted small font-weight-bold">TIEMPO PROMEDIO</div>
                            <div className="h1 font-weight-bold text-main m-0">
                                {averageAttentionTime || 0} <span className="h4 text-muted">min</span>
                            </div>
                            <div className="metric-trend">Meta: 8.5 min</div>
                        </div>

                        <div className="mt-auto pt-4 border-top border-secondary opacity-80">
                            <button className="btn-premium-sidebar w-100 mb-3" onClick={handleExportCSV}>
                                <FontAwesomeIcon icon={faFileExcel} className="mr-2 text-success" /> EXPORTAR EXCEL (CSV)
                            </button>
                            <div className="p-3 rounded -glass text-center">
                                <small className="text-muted d-block mb-1">DENTRO DEL SECTOR</small>
                                <span className="font-weight-bold text-primary-light">
                                    {availableTypes.find(t => String(t.TipoId) === String(activeType || typeIdsArray[0]))?.TipoNombre || 'Cargando...'}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </Col>
            </Row>

            {/* Modal remains largely the same but with premium style integration */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                size="lg"
                backdrop="static"
                contentClassName="modal-finish-premium"
            >
                <Modal.Header closeButton className="modal-finish-header">
                    <div className="w-100">
                        <Modal.Title className="modal-finish-title h4 mb-0">
                            Finalizar Atención - <span className="text-primary">{currentTurn?.TurnoNumero}</span>
                        </Modal.Title>
                        <div className="modal-finish-sector-badge">
                            {availableTypes.find(t => String(t.TipoId) === String(currentTurn?.TipoId))?.TipoNombre}
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="modal-finish-body">
                    <Form>
                        <div className="modal-finish-section-title">
                            <FontAwesomeIcon icon={faCheckCircle} /> DATOS DE LA ATENCIÓN
                        </div>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-4">
                                    <Form.Label className="modal-finish-label">REFERENCIA 1 (ID/DNI)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ingrese dato..."
                                        className="modal-finish-input"
                                        value={references.ref1}
                                        onChange={(e) => setReferences({ ...references, ref1: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-4">
                                    <Form.Label className="modal-finish-label">REFERENCIA 2 (NOMBRE)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ingrese dato..."
                                        className="modal-finish-input"
                                        value={references.ref2}
                                        onChange={(e) => setReferences({ ...references, ref2: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group className="mb-0">
                                    <Form.Label className="modal-finish-label">OBSERVACIONES INTERNAS</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Detalles de la consulta..."
                                        className="modal-finish-input"
                                        value={references.obs}
                                        onChange={(e) => setReferences({ ...references, obs: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="modal-finish-derivation-box mt-5">
                            <div className="modal-finish-section-title">
                                <FontAwesomeIcon icon={faExchangeAlt} /> DERIVACIÓN A OTRO SECTOR
                            </div>
                            <Form.Control
                                as="select"
                                className="modal-finish-input w-100"
                                value={targetType}
                                onChange={(e) => setTargetType(e.target.value)}
                                style={{ appearance: 'auto' }}
                            >
                                <option value="">Finalizar atención aquí (No derivar)</option>
                                {availableTypes.filter(t => String(t.TipoId) !== String(activeType || typeIdsArray[0])).map(t => (
                                    <option key={t.TipoId} value={t.TipoId}>{t.TipoNombre} ({t.TipoAcronimo})</option>
                                ))}
                            </Form.Control>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="modal-finish-footer">
                    <button
                        className="btn-premium-outline"
                        onClick={() => setShowModal(false)}
                    >
                        CANCELAR
                    </button>

                    <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                        {targetType && (
                            <button
                                className="btn-premium-solid"
                                style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid var(--turnero-primary)', color: 'white' }}
                                onClick={handleDeriveTurn}
                                disabled={loading}
                            >
                                {loading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faExchangeAlt} className="mr-2" /> DERIVAR</>}
                            </button>
                        )}
                        <button
                            className="btn-premium-outline"
                            onClick={handleFinishOnly}
                            disabled={loading}
                        >
                            {loading ? <Spinner animation="border" size="sm" /> : (
                                <><FontAwesomeIcon icon={faCheckCircle} className="mr-2" /> SOLO FINALIZAR</>
                            )}
                        </button>

                        <button
                            className="btn-premium-solid"
                            onClick={handleFinishAndNext}
                            disabled={loading}
                        >
                            {loading ? <Spinner animation="border" size="sm" /> : (
                                <><FontAwesomeIcon icon={faPlayCircle} className="mr-2" /> FINALIZAR Y LLAMAR SIGUIENTE</>
                            )}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StaffConsole;
